import React, { useMemo } from 'react';
import { User } from '../../../types';
import { PACKS } from '../../../data/packs';

type Props = {
  students: User[];
};

const RandomTab: React.FC<Props> = ({ students }) => {
  const { topMuses, topPacks } = useMemo(() => {
    const museCounts = new Map<string, { count: number; img?: string }>();
    const packMuseCounts = new Map<string, number>();

    students.forEach((student) => {
      const selected: any = (student as any).selectedMuse;
      if (selected && typeof selected === 'object' && typeof selected.name === 'string') {
        const key = String(selected.name);
        const existing = museCounts.get(key) || { count: 0, img: selected.img };
        museCounts.set(key, {
          count: existing.count + 1,
          img: existing.img || selected.img,
        });
      }

      const muses: any[] = Array.isArray((student as any).muses) ? (student as any).muses : [];
      muses.forEach((m) => {
        const packId = typeof m?.pack === 'string' ? m.pack : undefined;
        if (!packId) return;
        packMuseCounts.set(packId, (packMuseCounts.get(packId) || 0) + 1);
      });
    });

    const topMuses = Array.from(museCounts.entries())
      .map(([name, info]) => ({
        name,
        count: info.count,
        img: info.img,
      }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
      .slice(0, 10);

    const topPacks = Array.from(packMuseCounts.entries())
      .map(([packId, museCount]) => {
        const pack = PACKS[packId];
        const packName = pack?.name || packId;
        const approxPurchases = Math.round(museCount / 3);
        return {
          packId,
          packName,
          museCount,
          approxPurchases,
          price: typeof pack?.price === 'number' ? pack.price : null,
        };
      })
      .sort((a, b) => b.approxPurchases - a.approxPurchases || a.packName.localeCompare(b.packName))
      .slice(0, 10);

    return { topMuses, topPacks };
  }, [students]);

  return (
    <div className="random-stats-tab" style={{ width: '100%' }}>
      <div
        className="admin-content-card"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr)',
          gap: '1.5rem',
          alignItems: 'flex-start',
          width: '100%',
        }}
      >
        {/* Top Selected Muses */}
        <div>
          <h3 style={{ marginTop: 0 }}>🎲 Top 10 Selected Muses (Right Now)</h3>
          <p style={{ color: '#6b7280', marginTop: 0, marginBottom: '0.75rem' }}>
            Counts are based on each student&apos;s current selected muse.
          </p>
          {topMuses.length === 0 ? (
            <div style={{ padding: '1rem', color: '#6b7280' }}>No students with a selected muse yet.</div>
          ) : (
            <div className="table-wrapper" style={{ width: '100%', overflowX: 'auto' }}>
              <table className="reservations-table" style={{ width: '100%', minWidth: 420 }}>
                <thead>
                  <tr>
                    <th style={{ width: 56 }}>#</th>
                    <th style={{ minWidth: 200 }}>Muse</th>
                    <th style={{ width: 140, textAlign: 'right' }}>Selected</th>
                  </tr>
                </thead>
                <tbody>
                  {topMuses.map((muse, index) => (
                    <tr key={muse.name}>
                      <td>{index + 1}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {muse.img && (
                            <img
                              src={muse.img}
                              alt={muse.name}
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: 6,
                                objectFit: 'cover',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                              }}
                            />
                          )}
                          <span>{muse.name}</span>
                        </div>
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{muse.count.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Top Packs */}
        <div>
          <h3 style={{ marginTop: 0 }}>🃏 Top 10 Muse Packs (By Purchases)</h3>
          <p style={{ color: '#6b7280', marginTop: 0, marginBottom: '0.75rem' }}>
            Purchases are estimated from total cards owned per pack (3 cards per pack).
          </p>
          {topPacks.length === 0 ? (
            <div style={{ padding: '1rem', color: '#6b7280' }}>No pack data found from student collections yet.</div>
          ) : (
            <div className="table-wrapper" style={{ width: '100%', overflowX: 'auto' }}>
              <table className="reservations-table" style={{ width: '100%', minWidth: 520 }}>
                <thead>
                  <tr>
                    <th style={{ width: 56 }}>#</th>
                    <th style={{ minWidth: 200 }}>Pack</th>
                    <th style={{ width: 120, textAlign: 'right' }}>Price</th>
                    <th style={{ width: 140, textAlign: 'right' }}>Packs Bought</th>
                    <th style={{ width: 120, textAlign: 'right' }}>Cards</th>
                  </tr>
                </thead>
                <tbody>
                  {topPacks.map((pack, index) => (
                    <tr key={pack.packId}>
                      <td>{index + 1}</td>
                      <td>{pack.packName}</td>
                      <td style={{ textAlign: 'right' }}>
                        {pack.price != null ? (
                          <span>
                            💰 {pack.price.toLocaleString()}
                          </span>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>—</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>
                        {pack.approxPurchases.toLocaleString()}
                      </td>
                      <td style={{ textAlign: 'right' }}>{pack.museCount.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RandomTab;


